/**
 * TimelineActivityItem - Item `kind: 'activity'` da Timeline V2 do ticket.
 *
 * Consome o `TicketTimelineItem` do novo endpoint `GET /tickets/:id/timeline`
 * (Spec A Task 3/8). Desde o fix pós-review 2 (E2E live, 2026-07-12),
 * `item.activityType` traz o `action` GRANULAR da activity
 * (`comment_added`, `internal_note_added`, estratégias de atribuição, ...)
 * com fallback no `type` grosso (`communication`, `assignment`, ...) para
 * docs legados sem `action` — o mapa `ACTIVITY_TYPE_VISUAL` cobre os dois
 * conjuntos de chaves. Valores granulares REAIS verificados no backend:
 * - `activity-builder.ts:53` → `comment_added` | `internal_note_added`
 * - `tickets/service.ts:1399` (`recordAssignmentActivity`) → `action:
 *   ctx.strategy`, union `AssignmentStrategy` de `@troia-v3/types`
 *   (`manual | sequential | fixed_operator | fixed_team | shift |
 *   availability | last_interaction | routing_rule | ai_tool | workflow`)
 * - `POST /activities/log/ticket` (Zod `activities/validation.ts:211`) →
 *   `created | updated | assigned | resolved | closed | reopened`
 * O texto específico ("Prioridade alterada de Média para Alta") continua
 * vindo pronto e formatado em pt-BR em `item.content`.
 *
 * Nota: `workflow` colide entre os dois conjuntos (strategy de atribuição
 * via workflow × type grosso legado) — visual único zap/âmbar "Automação"
 * serve semanticamente aos dois casos.
 *
 * Distinção nota interna vs. comentário público é SEMPRE via `item.isInternal`
 * (nunca via `activityType`). Visual PRESERVADO da identidade atual do
 * módulo — roxo, badge "Nota Interna" com ícone de cadeado, borda tracejada
 * — únicos lugares do código com essa cor definida hoje são
 * `TicketActivityItem.tsx` e `TicketEditor.tsx` (o mockup T1 da spec cita
 * "amarelo/roxo atual" de forma ambígua, mas o código-fonte real nunca usou
 * amarelo para nota interna — decisão registrada no relatório da Task 8).
 */
import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { TicketTimelineItem, ActivityAttachment } from '@troia-v3/types';
import { TICKET_STATUS_CHANGE_ACTION } from '@troia-v3/types';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import { SafeHtml } from './SafeHtml';

export interface TimelineActivityItemProps {
  item: TicketTimelineItem;
}

interface ActivityVisual {
  icon: IconName;
  color: string;
  label: string;
}

/** Visual compartilhado das atribuições (todas as `AssignmentStrategy`). */
const ASSIGNMENT_VISUAL: ActivityVisual = { icon: 'user-plus', color: '#6366F1', label: 'Atribuição' };

const ACTIVITY_TYPE_VISUAL: Record<string, ActivityVisual> = {
  // ── Actions granulares (backend retorna `activity.action` desde o fix
  //    pós-review 2; valores verificados — ver comentário do topo) ─────────
  comment_added: { icon: 'message-square', color: '#3B82F6', label: 'Comentário' },
  internal_note_added: { icon: 'lock', color: '#8B5CF6', label: 'Nota interna' },
  // Trilha de mudança de etapa (Fase 0 do motor de SLA) — `action` granular
  // gravado por `buildTicketStatusChangeActivity` no backend.
  [TICKET_STATUS_CHANGE_ACTION]: { icon: 'arrow-right', color: '#0EA5E9', label: 'Mudança de etapa' },
  // recordAssignmentActivity grava `action: ctx.strategy` (AssignmentStrategy)
  manual: ASSIGNMENT_VISUAL,
  sequential: ASSIGNMENT_VISUAL,
  fixed_operator: ASSIGNMENT_VISUAL,
  fixed_team: ASSIGNMENT_VISUAL,
  shift: ASSIGNMENT_VISUAL,
  availability: ASSIGNMENT_VISUAL,
  last_interaction: ASSIGNMENT_VISUAL,
  routing_rule: ASSIGNMENT_VISUAL,
  ai_tool: { icon: 'user-plus', color: '#6366F1', label: 'Atribuição pela IA' },
  // POST /activities/log/ticket (enum Zod)
  created: { icon: 'plus', color: '#10B981', label: 'Criado' },
  updated: { icon: 'edit', color: '#6366F1', label: 'Atualizado' },
  assigned: ASSIGNMENT_VISUAL,
  resolved: { icon: 'check-circle', color: '#10B981', label: 'Resolvido' },
  closed: { icon: 'x-circle', color: '#6B7280', label: 'Fechado' },
  reopened: { icon: 'refresh', color: '#F59E0B', label: 'Reaberto' },
  // ── Fallback: `type` grosso (docs legados sem `action`) ─────────────────
  creation: { icon: 'plus', color: '#10B981', label: 'Criação' },
  modification: { icon: 'edit', color: '#6366F1', label: 'Alteração' },
  deletion: { icon: 'trash', color: '#EF4444', label: 'Exclusão' },
  assignment: ASSIGNMENT_VISUAL,
  status_change: { icon: 'refresh', color: '#10B981', label: 'Mudança de status' },
  communication: { icon: 'message-square', color: '#3B82F6', label: 'Comunicação' },
  interaction: { icon: 'activity', color: '#8B5CF6', label: 'Interação' },
  system: { icon: 'settings', color: '#6B7280', label: 'Sistema' },
  integration: { icon: 'link', color: '#6B7280', label: 'Integração' },
  // `workflow` cobre AMBOS: strategy de atribuição via workflow E type grosso
  workflow: { icon: 'zap', color: '#F59E0B', label: 'Automação' },
};

const DEFAULT_ACTIVITY_VISUAL: ActivityVisual = { icon: 'activity', color: '#6B7280', label: 'Atividade' };

/**
 * Tipos que SEMPRE renderizam como linha compacta, mesmo com ator humano e
 * conteúdo — são eventos do chamado, não mensagens. A regra geral
 * (`actor.type === 'system' || !content`) não os alcança porque a trilha de
 * etapa é movida por gente e carrega a frase pronta em `content`.
 */
const COMPACT_ACTIVITY_TYPES = new Set<string>([TICKET_STATUS_CHANGE_ACTION]);

function getActivityVisual(activityType?: string): ActivityVisual {
  if (!activityType) return DEFAULT_ACTIVITY_VISUAL;
  return ACTIVITY_TYPE_VISUAL[activityType] ?? DEFAULT_ACTIVITY_VISUAL;
}

function formatTime(dateString: string): string {
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: ptBR });
  } catch {
    return 'Data inválida';
  }
}

const EXT_COLOR_MAP: Record<string, string> = {
  pdf: '#ef4444',
  doc: '#3b82f6',
  docx: '#3b82f6',
  xls: '#10b981',
  xlsx: '#10b981',
  ppt: '#f97316',
  pptx: '#f97316',
  txt: '#6b7280',
  csv: '#10b981',
  zip: '#a855f7',
  rar: '#a855f7',
};

function extractExt(filename: string): string {
  const match = /\.([a-zA-Z0-9]+)$/.exec(filename);
  return match?.[1] ?? 'file';
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export interface TimelineAttachmentsProps {
  attachments: ActivityAttachment[];
  /** Prefixo de `data-testid` do item pai (ex.: `ticket-timeline-activity-<id>`). */
  testIdPrefix: string;
}

/**
 * Anexos de activity/e-mail da timeline — imagens viram thumbnail clicável
 * (abre original em nova aba), demais tipos viram chip (badge de extensão
 * colorido + nome + tamanho, com download). Reusado por `TimelineEmailItem`
 * (mesmo shape `ActivityAttachment` em ambos os `kind`).
 */
export const TimelineAttachments: React.FC<TimelineAttachmentsProps> = ({ attachments, testIdPrefix }) => {
  if (attachments.length === 0) return null;

  return (
    <div className="mt-3 flex flex-wrap gap-2" data-testid={`${testIdPrefix}-attachments`}>
      {attachments.map((attachment, index) => {
        const isImage = attachment.mimeType?.startsWith('image/') ?? false;
        const key = `${attachment.url}-${index}`;

        if (isImage) {
          return (
            <a
              key={key}
              href={attachment.url}
              target="_blank"
              rel="noopener noreferrer"
              title={attachment.filename}
              data-testid={`${testIdPrefix}-attachment-image-${index}`}
              className="block flex-shrink-0 rounded-lg border overflow-hidden"
              style={{ borderColor: 'var(--theme-card-border)', width: 96, height: 96 }}
            >
              <img src={attachment.url} alt={attachment.filename} className="w-full h-full object-cover" />
            </a>
          );
        }

        const ext = extractExt(attachment.filename);
        const extColor = EXT_COLOR_MAP[ext.toLowerCase()] ?? '#6B7280';

        return (
          <a
            key={key}
            href={attachment.url}
            target="_blank"
            rel="noopener noreferrer"
            download={attachment.filename}
            title={attachment.filename}
            data-testid={`${testIdPrefix}-attachment-${index}`}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors"
            style={{ backgroundColor: 'var(--theme-button-ghost)', color: 'var(--theme-card-text)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost)';
            }}
          >
            <span
              className="flex items-center justify-center rounded flex-shrink-0 text-[9px] font-extrabold text-white"
              style={{ width: 20, height: 20, backgroundColor: extColor }}
            >
              {ext.slice(0, 3).toUpperCase()}
            </span>
            <span className="max-w-[160px] truncate">{attachment.filename}</span>
            {attachment.size != null && (
              <span style={{ color: 'var(--theme-muted-text)' }}>{formatFileSize(attachment.size)}</span>
            )}
          </a>
        );
      })}
    </div>
  );
};

TimelineAttachments.displayName = 'TimelineAttachments';

export const TimelineActivityItem: React.FC<TimelineActivityItemProps> = ({ item }) => {
  const isInternalNote = item.isInternal === true;
  const isCompactLine =
    COMPACT_ACTIVITY_TYPES.has(item.activityType ?? '') ||
    item.actor?.type === 'system' ||
    !item.content;
  const visual = getActivityVisual(item.activityType);
  const attachments = item.attachments ?? [];
  const metadataFrom = typeof item.metadata?.from === 'string' ? item.metadata.from : undefined;
  const metadataTo = typeof item.metadata?.to === 'string' ? item.metadata.to : undefined;

  // Atividades de sistema / sem conteúdo (mudança de status, atribuição,
  // SLA, tags, anexo) — linha compacta com borda colorida à esquerda.
  if (isCompactLine) {
    return (
      <div
        data-testid={`ticket-timeline-activity-${item.id}`}
        className="flex items-center gap-3 px-4 py-2 rounded-lg"
        style={{ backgroundColor: `${visual.color}10`, borderLeft: `3px solid ${visual.color}` }}
      >
        <Icon name={visual.icon} className="w-4 h-4 flex-shrink-0" style={{ color: visual.color }} />
        <span className="text-sm" style={{ color: 'var(--theme-card-text)' }}>
          {item.content || visual.label}
          {metadataFrom && metadataTo && (
            <span style={{ color: 'var(--theme-muted-text)' }}>
              {' '}de <strong>{metadataFrom}</strong> para <strong>{metadataTo}</strong>
            </span>
          )}
        </span>
        {item.actor?.type === 'user' && item.actor.name && (
          <span className="text-xs flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }}>
            por {item.actor.name}
          </span>
        )}
        <span className="text-xs ml-auto flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }}>
          {formatTime(item.createdAt)}
        </span>
      </div>
    );
  }

  // Mensagem/comentário público ou nota interna — card
  return (
    <div
      data-testid={`ticket-timeline-activity-${item.id}`}
      className="rounded-lg p-4"
      style={{
        backgroundColor: isInternalNote ? 'rgba(139, 92, 246, 0.05)' : 'var(--theme-card-background)',
        border: isInternalNote ? '1px dashed rgba(139, 92, 246, 0.3)' : '1px solid var(--theme-card-border)',
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <Avatar className="w-8 h-8" isSquircle>
          <AvatarFallback isSquircle>
            <span className="text-xs font-semibold" style={{ color: 'var(--theme-primary)' }}>
              {item.actor?.name?.charAt(0)?.toUpperCase() || '?'}
            </span>
          </AvatarFallback>
        </Avatar>

        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
              {item.actor?.name || 'Sistema'}
            </span>
            {isInternalNote && (
              <span
                data-testid={`ticket-timeline-activity-${item.id}-internal-badge`}
                className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full"
                style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
              >
                <Icon name="lock" className="w-3 h-3" />
                Nota Interna
              </span>
            )}
          </div>
          <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
            {formatTime(item.createdAt)}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="text-sm" style={{ color: 'var(--theme-card-text)' }}>
        {item.contentFormat === 'html' ? (
          <SafeHtml html={item.content ?? ''} data-testid={`ticket-timeline-activity-${item.id}-html`} />
        ) : (
          <div className="whitespace-pre-wrap">{item.content}</div>
        )}
      </div>

      {/* Attachments */}
      <TimelineAttachments attachments={attachments} testIdPrefix={`ticket-timeline-activity-${item.id}`} />
    </div>
  );
};

TimelineActivityItem.displayName = 'TimelineActivityItem';
